import React, { memo, Fragment } from 'react'
import { useProduct, ProductProps } from './useProduct'
import PlantType from 'types/PlantType'
import PriceList from './PriceList/PriceList'
import ImageViewer from './ImageViewer/ImageViewer'
import styles from './Product.module.scss'
import NutritionItem from './NutritionItem/NutritionItem'
import Popular from './Popular/Popular'
import PlantBadge from './PlantBadge/PlantBadge'
import GlutenFree from './GlutenFree/GlutenFree'
import Halal from './Halal/Halal'
import AddToCart from './AddToCart/AddToCart'

const Product: React.FC<ProductProps> = (props: ProductProps) => {
  const {
    id,
    name,
    currency,
    price,
    hasNutrition,
    items,
    priceList,
    image,
    description,
    showTooltip,
    isPopular,
    isVegan,
    isVegetarian,
    isGlutenFree,
    isHalal,
    plantType,
    isClickable,
    isImageViewerOpen,
    isAddToCartActive,
    handleMouseEnter,
    handleMouseLeave,
    handleImageClick,
    handleCloseImageViewer,
  } = useProduct(props)

  return (
    <div
      className={`${styles.product} ${
        isClickable ? styles['is-clickable'] : ''
      }`}
    >
      {isPopular && <Popular />}
      {isHalal && (
        <Halal isBadgeActive={isVegan || isVegetarian || isGlutenFree} />
      )}
      {isVegan || isVegetarian ? (
        <PlantBadge type={plantType as PlantType} />
      ) : (
        <Fragment />
      )}
      {isGlutenFree && (
        <GlutenFree isVegan={isVegan} isVegetarian={isVegetarian} />
      )}

      {hasNutrition ? (
        <div className={styles.nutrition_container}>
          {items.map(
            ({ title, item, unit }) =>
              item &&
              Number(item) > 0 && (
                <NutritionItem
                  key={title}
                  title={title}
                  item={item}
                  unit={unit}
                />
              )
          )}
        </div>
      ) : (
        <Fragment />
      )}

      {isAddToCartActive && <AddToCart productId={id} />}
      <img
        src={image}
        alt='product background'
        className={styles.image}
        loading='lazy'
        onClick={handleImageClick}
      />
      <div className={styles.product_content}>
        <div className={styles.title_price_row}>
          <div className={styles.product_name}>
            {name}
            {description && (
              <span
                className={styles.info_icon}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i className='bi bi-info-circle'></i>
              </span>
            )}
          </div>

          <div className={styles.price_container}>
            {price ? (
              <div className={styles.product_price}>
                <span className={styles.currency}>{currency}</span>
                {price}
              </div>
            ) : (
              priceList && (
                <PriceList currency={currency} priceList={priceList} />
              )
            )}
          </div>
        </div>
        <div
          className={`${styles.description_wrapper} ${
            showTooltip ? styles.is_open : ''
          }`}
        >
          <div className={styles.description_inner}>{description}</div>
        </div>
      </div>
      {isClickable && isImageViewerOpen && (
        <ImageViewer image={image} handleClose={handleCloseImageViewer} />
      )}
    </div>
  )
}

export default memo(Product)
